import { Button, Spacer } from '@odekoteam/doppio';

import { GProviderDispatchStatus } from 'src/graphql/types/generated';
import { formatDateTo12Hour } from 'src/utils/date';
import { providerDispatchStatusLabel } from 'src/utils/providerDispatchStatus';
import {
  isQuoteExpired,
  providerCanceledUnmarkMessage,
  ProviderQuote,
  quoteProviderLabel,
  quoteTotalLabel,
} from 'src/utils/thirdPartyDelivery';
import Spinner from '../../Spinner';
import { DialogView } from './dialogView';
import { QuoteList } from './QuoteList';
import { PolledDispatch } from './useProviderDispatchPoll';

export const KEEP_BUTTON_ID = 'mark-route-for-provider-keep';
export const CANCEL_ON_PROVIDER_ID = 'mark-route-for-provider-cancel';
export const PRIMARY_ROW_TEST_ID = 'mark-route-for-provider-primary-row';
// Full width in the wrapping action row, so the primary sits alone above the rest.
export const primaryRowSx = { flexBasis: '100%' };

export const bodyTextSx = {
  variant: 'text.paragraphMdDefault',
  color: 'secondaryText',
  margin: 0,
};

export const errorTextSx = {
  variant: 'text.paragraphSmDefault',
  color: 'errorText',
  display: 'block',
  marginTop: '8px',
};

export type ViewContent = {
  heading: string;
  body: JSX.Element | null;
  actions: JSX.Element;
  // Announced by the dialog's live region; errors go through its message.
  statusLine?: string;
};

// What a view needs from the dialog: what the poll read, the dialog's own
// state, and its handlers. The dialog owns every request.
export type ViewContext = {
  routeName: string;
  providerName: string;
  timeZone: string;
  isSubmitting: boolean;
  isPending: boolean;
  stillWaiting: boolean;
  claimStale: boolean;
  record: PolledDispatch | undefined;
  stopCount: number;
  selectedQuoteId: string | undefined;
  heldQuote: ProviderQuote | undefined;
  formFields: JSX.Element;
  canMark: boolean;
  changeFields: JSX.Element;
  changeNeedsTime: boolean;
  onClose: () => void;
  onMark: () => Promise<void>;
  onRequote: () => Promise<unknown>;
  onRequoteFromChange: () => Promise<void>;
  onBackFromChange: () => void;
  onDispatch: (quote: ProviderQuote) => Promise<void>;
  onOpenChange: () => void;
  onSelectQuote: (id: string) => void;
  onKeep: () => void;
  // Opens the cancel confirm; the dialog sends the cancel from there.
  onCancelOnProvider: () => void;
  // Opened from "Cancel on Burq": the route is dispatched, never one to send.
  openedToCancel: boolean;
};

const closeButton = (ctx: ViewContext): JSX.Element => (
  <Button
    variant="tertiary"
    id="mark-route-for-provider-close"
    onPress={ctx.onClose}
    isDisabled={ctx.isSubmitting}
  >
    Close
  </Button>
);

const keepButton = (ctx: ViewContext): JSX.Element => (
  <Button
    variant="secondary"
    id={KEEP_BUTTON_ID}
    onPress={ctx.onKeep}
    isDisabled={ctx.isSubmitting}
  >
    Keep with Odeko
  </Button>
);

const cancelOnProviderButton = (
  ctx: ViewContext,
  label = `Cancel on ${ctx.providerName} and keep with Odeko`,
): JSX.Element => (
  <div data-test-id={PRIMARY_ROW_TEST_ID} sx={primaryRowSx}>
    <Button
      variant="primary"
      id={CANCEL_ON_PROVIDER_ID}
      onPress={ctx.onCancelOnProvider}
      isDisabled={ctx.isSubmitting}
    >
      {label}
    </Button>
  </div>
);

const primaryButton = (
  ctx: ViewContext,
  {
    label,
    busyLabel,
    onPress,
    isDisabled = false,
    fullRow = false,
  }: {
    label: string;
    busyLabel: string;
    onPress: () => unknown;
    isDisabled?: boolean;
    // Alone on the first row of the wrapping action row, the rest below: a
    // doppio Button label never wraps, so three buttons overrun the panel.
    fullRow?: boolean;
  },
): JSX.Element => {
  const button = (
    <Button
      variant="primary"
      id="mark-route-for-provider-action-button"
      onPress={onPress}
      isDisabled={ctx.isSubmitting || isDisabled}
    >
      {ctx.isSubmitting ? busyLabel : label}
    </Button>
  );
  return fullRow ? (
    <div data-test-id={PRIMARY_ROW_TEST_ID} sx={primaryRowSx}>
      {button}
    </div>
  ) : (
    button
  );
};

const recordError = (ctx: ViewContext): JSX.Element | null =>
  ctx.record?.error ? <span sx={errorTextSx}>{ctx.record.error}</span> : null;

const loadingView = (ctx: ViewContext): ViewContent => ({
  heading:
    ctx.isPending || ctx.openedToCancel
      ? `Checking ${ctx.routeName} with ${ctx.providerName}`
      : `Send ${ctx.routeName} to ${ctx.providerName}`,
  statusLine: ctx.isPending ? 'Waiting for an update' : 'Loading',
  body: <Spinner size={24} />,
  actions: closeButton(ctx),
});

const formView = (ctx: ViewContext, loadFailed: boolean): ViewContent => ({
  heading: `Send ${ctx.routeName} to ${ctx.providerName}`,
  body: (
    <>
      {loadFailed && (
        <span sx={errorTextSx}>
          {`Couldn't load the ${ctx.providerName} status. You can still get quotes.`}
        </span>
      )}
      <p sx={bodyTextSx}>
        {`${ctx.providerName} quotes the route once it has it. Nothing is dispatched until you pick a quote.`}
      </p>
      <Spacer height="$3" />
      {ctx.formFields}
    </>
  ),
  actions: (
    <>
      {primaryButton(ctx, {
        label: 'Get quotes',
        busyLabel: 'Sending...',
        onPress: ctx.onMark,
        isDisabled: !ctx.canMark,
      })}
      {closeButton(ctx)}
    </>
  ),
});

const pushFailedView = (ctx: ViewContext): ViewContent => ({
  heading: `Couldn't send ${ctx.routeName} to ${ctx.providerName}`,
  body: (
    <>
      {recordError(ctx)}
      <p sx={bodyTextSx}>
        {`Nothing was sent, ${ctx.routeName} is still a regular route.`}
      </p>
      <Spacer height="$3" />
      {ctx.formFields}
    </>
  ),
  actions: (
    <>
      {primaryButton(ctx, {
        label: 'Try again',
        busyLabel: 'Sending...',
        onPress: ctx.onMark,
        isDisabled: !ctx.canMark,
      })}
      {/* The failed push already made it an Odeko route. */}
      <Button
        variant="secondary"
        onPress={ctx.onClose}
        isDisabled={ctx.isSubmitting}
      >
        Keep with Odeko
      </Button>
    </>
  ),
});

const waitingStatusLine = (ctx: ViewContext, pushing: boolean): string => {
  if (ctx.stillWaiting) {
    return `Still waiting on ${ctx.providerName}. You can close this and come back.`;
  }
  return pushing ? 'Sending' : 'Getting quotes';
};

const waitingView = (
  ctx: ViewContext,
  status: GProviderDispatchStatus,
): ViewContent => {
  const pushing = status === GProviderDispatchStatus.Pushing;
  return {
    heading: pushing
      ? `Sending ${ctx.routeName} to ${ctx.providerName}`
      : `Getting quotes for ${ctx.routeName}`,
    statusLine: waitingStatusLine(ctx, pushing),
    body: <Spinner size={24} />,
    // The provider's route does not exist until the push is done; LDS
    // refuses an unmark while it runs.
    actions: (
      <>
        {!pushing && keepButton(ctx)}
        {closeButton(ctx)}
      </>
    ),
  };
};

const pickupText = (ctx: ViewContext): string =>
  ctx.record?.pickupAt
    ? formatDateTo12Hour(new Date(ctx.record.pickupAt), ctx.timeZone)
    : 'ASAP';

const dispatchOrRequote = (ctx: ViewContext): JSX.Element => {
  const quote = ctx.record?.quotes.find(({ id }) => id === ctx.selectedQuoteId);
  if (!quote || isQuoteExpired(quote)) {
    return primaryButton(ctx, {
      label: 'Request quotes again',
      busyLabel: 'Requesting...',
      onPress: ctx.onRequote,
      fullRow: true,
    });
  }
  return primaryButton(ctx, {
    label: `Dispatch with ${quoteProviderLabel(quote)} · ${quoteTotalLabel(
      quote,
    )}`,
    busyLabel: 'Dispatching...',
    onPress: () => ctx.onDispatch(quote),
    fullRow: true,
  });
};

const quotesView = (ctx: ViewContext): ViewContent => {
  const quotes = ctx.record?.quotes ?? [];
  return {
    heading: `Choose a courier for ${ctx.routeName}`,
    body: (
      <>
        {recordError(ctx)}
        {quotes.length === 0 ? (
          <p sx={bodyTextSx}>No usable quotes.</p>
        ) : (
          <QuoteList
            quotes={quotes}
            selectedId={ctx.selectedQuoteId}
            onSelect={ctx.onSelectQuote}
            timeZone={ctx.timeZone}
          />
        )}
        <Spacer height="$3" />
        <p sx={bodyTextSx}>
          {`${ctx.stopCount} stops · pickup ${pickupText(ctx)} `}
          <Button
            variant="tertiary"
            size="small"
            onPress={ctx.onOpenChange}
            isDisabled={ctx.isSubmitting}
          >
            Change
          </Button>
        </p>
        <p sx={bodyTextSx}>
          {`${ctx.providerName} books the courier when you dispatch.`}
        </p>
      </>
    ),
    actions: (
      <>
        {dispatchOrRequote(ctx)}
        {keepButton(ctx)}
        {closeButton(ctx)}
      </>
    ),
  };
};

const changeView = (ctx: ViewContext): ViewContent => ({
  heading: `Change the pickup for ${ctx.routeName}`,
  body: ctx.changeFields,
  actions: (
    <>
      {primaryButton(ctx, {
        label: 'Request quotes',
        busyLabel: 'Requesting...',
        onPress: ctx.onRequoteFromChange,
        isDisabled: ctx.changeNeedsTime,
      })}
      <Button
        variant="secondary"
        onPress={ctx.onBackFromChange}
        isDisabled={ctx.isSubmitting}
      >
        Back
      </Button>
    </>
  ),
});

const quoteFailedView = (ctx: ViewContext): ViewContent => ({
  heading: `${ctx.providerName} couldn't quote ${ctx.routeName}`,
  body: recordError(ctx),
  actions: (
    <>
      {primaryButton(ctx, {
        label: 'Try again',
        busyLabel: 'Requesting...',
        onPress: ctx.onRequote,
        fullRow: true,
      })}
      {keepButton(ctx)}
      {closeButton(ctx)}
    </>
  ),
});

const canceledOnProviderView = (ctx: ViewContext): ViewContent => ({
  heading: `${ctx.providerName} canceled ${ctx.routeName}`,
  body: recordError(ctx),
  actions: (
    <>
      {keepButton(ctx)}
      {closeButton(ctx)}
    </>
  ),
});

const dispatchingView = (ctx: ViewContext): ViewContent => {
  const { heldQuote } = ctx;
  return {
    heading: `Dispatching ${ctx.routeName}`,
    statusLine: `${ctx.providerName} has not confirmed yet.`,
    body: recordError(ctx),
    actions: (
      <>
        {ctx.claimStale &&
          heldQuote &&
          primaryButton(ctx, {
            label: 'Dispatch again',
            busyLabel: 'Dispatching...',
            onPress: () => ctx.onDispatch(heldQuote),
          })}
        {closeButton(ctx)}
      </>
    ),
  };
};

// Running, and LDS would send a cancel now. The record's error is the last
// cancel's outcome: Burq's refusal, or that Burq never confirmed it.
const runningView = (ctx: ViewContext): ViewContent => ({
  heading: `${ctx.routeName} is in progress`,
  body: recordError(ctx),
  actions: (
    <>
      {cancelOnProviderButton(ctx)}
      {closeButton(ctx)}
    </>
  ),
});

// The cancel is out. An error here means the request may have gone through
// unanswered; a cancel is offered again only once LDS would take one.
const cancelingView = (ctx: ViewContext, cancelable: boolean): ViewContent => ({
  heading: `Canceling ${ctx.routeName} on ${ctx.providerName}`,
  statusLine: `Waiting for ${ctx.providerName} to confirm`,
  body: recordError(ctx),
  actions: (
    <>
      {cancelable && cancelOnProviderButton(ctx, 'Cancel again')}
      {closeButton(ctx)}
    </>
  ),
});

const endedHeading = (
  ctx: ViewContext,
  view: Extract<DialogView, { kind: 'ended' }>,
): string => {
  if (view.reason === 'superseded') {
    return `${ctx.routeName} was sent again from another session`;
  }
  if (view.reason === 'backWithOdeko') {
    return `${ctx.routeName} is back with Odeko`;
  }
  const label = providerDispatchStatusLabel(
    view.status ?? GProviderDispatchStatus.InProgress,
  );
  return `${ctx.routeName} is ${label.toLowerCase()}`;
};

const endedView = (
  ctx: ViewContext,
  view: Extract<DialogView, { kind: 'ended' }>,
): ViewContent => ({
  heading: endedHeading(ctx, view),
  body:
    view.reason === 'superseded' ? (
      <p sx={bodyTextSx}>Close and reopen to see it.</p>
    ) : (
      recordError(ctx)
    ),
  actions: closeButton(ctx),
});

export const viewContent = (
  view: DialogView,
  changeOpen: boolean,
  ctx: ViewContext,
): ViewContent => {
  switch (view.kind) {
    case 'loading':
      return loadingView(ctx);
    case 'form':
      return formView(ctx, view.loadFailed);
    case 'pushFailed':
      return pushFailedView(ctx);
    case 'waiting':
      return waitingView(ctx, view.status);
    case 'quotes':
      return changeOpen ? changeView(ctx) : quotesView(ctx);
    case 'quoteFailed':
      return changeOpen ? changeView(ctx) : quoteFailedView(ctx);
    case 'canceledOnProvider':
      return canceledOnProviderView(ctx);
    case 'dispatching':
      return dispatchingView(ctx);
    case 'running':
      return runningView(ctx);
    case 'canceling':
      return cancelingView(ctx, view.cancelable);
    case 'ended':
      return endedView(ctx, view);
  }
};

// The Keep with Odeko confirm, a view of its own in place of the one it was
// opened from: the modal store holds one modal. `liveDispatch`: the provider
// canceled a dispatched route, so there is no draft to delete.
export const keepConfirm = (
  ctx: Pick<ViewContext, 'routeName' | 'providerName' | 'isSubmitting'> & {
    liveDispatch?: boolean;
  },
  onRemove: () => Promise<void>,
  onCancel: () => void,
): ViewContent => ({
  heading: ctx.liveDispatch
    ? `Keep ${ctx.routeName} with Odeko?`
    : `Remove ${ctx.routeName} from ${ctx.providerName}?`,
  body: (
    <p sx={bodyTextSx}>
      {ctx.liveDispatch
        ? providerCanceledUnmarkMessage(ctx.providerName)
        : 'Its draft and any quotes will be deleted, and it goes back to an Odeko route.'}
    </p>
  ),
  actions: (
    <>
      <Button
        variant="primary"
        onPress={onRemove}
        isDisabled={ctx.isSubmitting}
      >
        {ctx.isSubmitting
          ? 'Removing...'
          : ctx.liveDispatch
          ? 'Keep with Odeko'
          : 'Remove'}
      </Button>
      <Button
        variant="secondary"
        onPress={onCancel}
        isDisabled={ctx.isSubmitting}
      >
        Cancel
      </Button>
    </>
  ),
});

// The cancel confirm, a whole view like keepConfirm. Back returns to the view
// it stands over, or closes the dialog when it opened on this confirm.
export const cancelConfirm = (
  ctx: Pick<ViewContext, 'routeName' | 'providerName' | 'isSubmitting'>,
  onConfirm: () => Promise<void>,
  onBack: () => void,
): ViewContent => ({
  heading: `Cancel ${ctx.routeName} on ${ctx.providerName}?`,
  body: (
    <p sx={bodyTextSx}>
      {`${ctx.providerName} is asked to cancel the route. If it confirms, the route goes back to an Odeko route with its orders.`}
    </p>
  ),
  actions: (
    <>
      <Button
        variant="primary"
        onPress={onConfirm}
        isDisabled={ctx.isSubmitting}
      >
        {ctx.isSubmitting ? 'Canceling...' : `Cancel on ${ctx.providerName}`}
      </Button>
      <Button
        variant="secondary"
        onPress={onBack}
        isDisabled={ctx.isSubmitting}
      >
        Back
      </Button>
    </>
  ),
});
